/**
 * Icon path definitions
 * These correspond to the SVG files in assets/svg/
 * Paths are extracted from the SVG files for inline rendering with dynamic styling
 */

export interface SvgElement {
  tag: 'path' | 'rect' | 'circle' | 'ellipse' | 'polygon';
  attrs: Record<string, string>;
}

export interface IconDefinition {
  /** Single SVG path (simple icons) */
  path?: string;
  /** Multiple SVG elements (complex icons — use instead of path) */
  elements?: SvgElement[];
  strokeWidth?: number;
  viewBox?: string;
  /** If true, uses fill instead of stroke for single-path icons */
  fill?: boolean;
  /** If true, icon has its own colors and ignores the color prop */
  multiColor?: boolean;
  fillRule?: 'nonzero' | 'evenodd';
}

export const iconDefinitions: Record<string, IconDefinition> = {
  search: {
    path: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    strokeWidth: 2.5,
    viewBox: '0 0 24 24',
  },
  star: {
    // A single five-point star (extracted from the legacy 5-star review SVG).
    // Filled, so it takes the `color` prop — render one per rating position,
    // colouring filled vs empty.
    path: 'M37.9844 22.3076L61.4062 22.2578L42.4307 35.9883L49.7119 58.2441L30.791 44.4424L11.8721 58.249L19.1514 35.9883L0.179688 22.2627L23.5977 22.3076L30.7891 0.0195312L37.9844 22.3076Z',
    viewBox: '0 0 62 59',
    fill: true,
  },
  cross: {
    path: 'M6 18L18 6M6 6l12 12',
    strokeWidth: 2.5,
    viewBox: '0 0 24 24',
  },
  close: {
    path: 'M7.2514 5.5L10.6313 2.13889C11.1229 1.65 11.1229 0.916667 10.6313 0.427778C10.4469 0.183333 10.1397 0 9.8324 0C9.52514 0 9.21788 0.122222 8.97207 0.366667L5.53073 3.78889L2.15084 0.427778C1.65922 -0.0611112 0.860335 -0.0611112 0.430168 0.427778C0.184357 0.611111 0 0.916667 0 1.28333C0 1.65 0.122905 1.89444 0.368715 2.13889L3.7486 5.5L0.368715 8.86111C0.184357 9.10556 0 9.41111 0 9.77778C0 10.0833 0.122905 10.3889 0.368715 10.6333C0.614525 10.8778 0.921788 11 1.22905 11C1.53631 11 1.84358 10.8778 2.08939 10.6333L5.46927 7.27222L8.84916 10.6333C9.34078 11.1222 10.1397 11.1222 10.5698 10.6333C11.0615 10.1444 11.0615 9.35 10.5698 8.92222L7.2514 5.5Z',
    viewBox: '0 0 11 11',
    fill: true,
  },
  user: {
    path: 'M424.4,377.8c0,19.8-6,35.5-18.1,47c-12.1,11.5-28.1,17.2-48.1,17.2H141.5c-20,0-36-5.7-48.1-17.2  s-18.1-27.2-18.1-47c0-8.8,0.3-17.3,0.9-25.7c0.6-8.3,1.7-17.4,3.5-27c1.7-9.7,3.9-18.6,6.6-26.9s6.2-16.3,10.7-24.2  c4.5-7.9,9.6-14.5,15.4-20.1c5.8-5.5,12.9-10,21.2-13.3c8.3-3.3,17.6-5,27.6-5c1.5,0,5,1.8,10.4,5.3c5.5,3.6,11.6,7.5,18.5,11.9  c6.9,4.4,15.8,8.3,26.8,11.9c11,3.6,22,5.3,33.1,5.3s22.1-1.8,33.1-5.3c11-3.6,19.9-7.5,26.8-11.9c6.9-4.4,13-8.3,18.5-11.9  s8.9-5.3,10.4-5.3c10.1,0,19.3,1.7,27.6,5c8.3,3.3,15.4,7.7,21.2,13.3c5.8,5.5,10.9,12.2,15.4,20.1c4.5,7.9,8,15.9,10.7,24.2  s4.8,17.2,6.6,26.9c1.7,9.7,2.9,18.7,3.5,27C424.1,360.5,424.4,369.1,424.4,377.8z M345.1,156.4c0,26.3-9.3,48.7-27.9,67.3  s-41,27.9-67.3,27.9s-48.7-9.3-67.3-27.9s-27.9-41-27.9-67.3s9.3-48.7,27.9-67.3s41-27.9,67.3-27.9s48.7,9.3,67.3,27.9  S345.1,130.1,345.1,156.4z',
    viewBox: '0 0 500 500',
    fill: true,
  },
  logout: {
    path: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4m7 14l5-5-5-5m5 5H9',
    strokeWidth: 2,
    viewBox: '0 0 24 24',
  },
  support: {
    path: 'M725 1590 c-198 -31 -386 -141 -500 -291 -59 -78 -119 -203 -142 -295 -25 -103 -23 -277 5 -377 79 -281 289 -486 572 -558 82 -21 289 -18 365 5 242 73 397 200 504 413 53 106 75 203 75 333 0 141 -19 222 -79 345 -124 255 -366 414 -655 429 -47 3 -112 1 -145 -4z m242 -116 c41 -8 100 -26 130 -40 l55 -24 -87 -84 c-84 -80 -89 -83 -119 -75 -40 12 -182 12 -223 0 -30 -9 -35 -6 -112 71 -45 44 -81 83 -81 88 0 12 96 48 170 64 87 19 175 19 267 0z m8 -348 c67 -30 130 -95 164 -166 36 -76 36 -201 1 -275 -31 -64 -101 -134 -165 -165 -68 -34 -205 -35 -278 -2 -60 27 -136 103 -167 167 -35 74 -35 199 1 275 81 171 275 244 444 166z m-639 -77 l82 -81 -11 -37 c-13 -47 -13 -166 1 -214 l11 -39 -87 -84 c-97 -94 -89 -96 -129 19 -33 97 -42 253 -19 350 17 71 54 167 65 167 3 0 42 -37 87 -81z m1113 32 c62 -144 67 -330 14 -475 -16 -44 -33 -81 -39 -83 -5 -2 -46 32 -91 76 l-82 80 11 38 c13 45 13 176 0 218 -9 29 -5 34 72 113 44 45 83 82 87 82 4 0 16 -22 28 -49z m-681 -692 c48 -9 82 -8 139 1 l76 12 80 -80 c44 -44 77 -84 73 -90 -3 -6 -43 -23 -89 -39 -75 -25 -94 -28 -217 -27 -128 0 -140 2 -223 33 l-89 34 83 83 c46 46 88 84 93 84 5 0 38 -5 74 -11z',
    viewBox: '0 0 1670 1630',
    fill: true,
  },
  hamburger: {
    path: 'M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z',
    viewBox: '0 0 640 640',
    fill: true,
  },
  facebook: {
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l98.2 0 0-145.8-52.8 0 0-78.2 52.8 0 0-33.7c0-87.1 39.4-127.5 125-127.5 16.2 0 44.2 3.2 55.7 6.4l0 70.8c-6-.6-16.5-1-29.6-1-42 0-58.2 15.9-58.2 57.2l0 27.8 83.6 0-14.4 78.2-69.3 0 0 145.8 129 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32z',
    viewBox: '0 0 448 512',
    fill: true,
  },
  linkedin: {
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32zm5 170.2l66.5 0 0 213.8-66.5 0 0-213.8zm71.7-67.7a38.5 38.5 0 1 1 -77 0 38.5 38.5 0 1 1 77 0zM317.9 416l0-104c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9l0 105.8-66.4 0 0-213.8 63.7 0 0 29.2 .9 0c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9l0 117.2-66.4 0z',
    viewBox: '0 0 448 512',
    fill: true,
  },
  x: {
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32zm297.1 84l-103.8 118.6 122.1 161.4-95.6 0-74.8-97.9-85.7 97.9-47.5 0 111-126.9-117.1-153.1 98 0 67.7 89.5 78.2-89.5 47.5 0zM323.3 367.6l-169.9-224.7-28.3 0 171.8 224.7 26.4 0z',
    viewBox: '0 0 448 512',
    fill: true,
  },
  basket: {
    viewBox: '0 0 26 31',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M13.5 0C17.6421 0 21 3.35786 21 7.5C21 7.66806 20.9923 7.83469 20.9814 8H21.7373C23.2756 8 24.5539 9.15996 24.6689 10.6602L25.9922 27.9102C26.12 29.5779 24.7706 30.9999 23.0605 31H2.94043C1.23032 31 -0.11901 29.5779 0.00878906 27.9102L1.33105 10.6602C1.44609 9.16006 2.72457 8.00014 4.2627 8H6.01855C6.00767 7.83469 6 7.66806 6 7.5C6 3.3579 9.35791 5.58258e-05 13.5 0ZM13.5 2C10.4625 2.00006 8 4.46247 8 7.5C8 7.66857 8.00859 7.83529 8.02344 8H18.9766C18.9914 7.83529 19 7.66858 19 7.5C19 4.46243 16.5376 2 13.5 2Z',
          fill: '#35635A',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M17.7797 27C17.678 27 17.5254 27 17.4237 27C17.322 27 17.1695 27 17.0678 26.948L16.6102 26.8959V13.1041L17.0678 13.052C17.1695 13.052 17.2712 13.052 17.4237 13C17.678 13 17.8814 13 18.1864 13C18.339 13 18.4407 13 18.5424 13.052L19 13.1041V26.8959L18.5424 26.948C18.4407 26.948 18.339 26.948 18.1864 27C18.0847 27 17.9322 27 17.7797 27ZM8.16949 27C8.0678 27 7.91525 27 7.81356 27C7.71186 27 7.55932 27 7.45763 26.948L7 26.8959V13.1041L7.45763 13.052C7.55932 13.052 7.66102 13.052 7.81356 13C8.0678 13 8.27119 13 8.57627 13C8.72881 13 8.83051 13 8.9322 13.052L9.38983 13.1041V26.8959L8.9322 26.948C8.83051 26.948 8.72881 26.948 8.57627 27C8.42373 27 8.27119 27 8.16949 27ZM9.94915 19.0372C9.94915 18.9331 9.94915 18.881 9.94915 18.777C9.94915 18.7249 9.94915 18.6208 10 18.5688L10.0508 18.2565H15.8983L15.9492 18.5688C15.9492 18.6208 15.9492 18.7249 16 18.777C16 18.9331 16 19.0892 16 19.2454C16 19.3494 16 19.4015 15.9492 19.4535L15.8983 19.7658H10.1017L10.0508 19.4535C10.0508 19.4015 10.0508 19.2974 10 19.2454C10 19.1933 9.94915 19.0892 9.94915 19.0372ZM9.94915 21.223C9.94915 21.119 9.94915 21.0669 9.94915 20.9628C9.94915 20.9108 9.94915 20.8067 10 20.7546L10.0508 20.4424H15.8983L15.9492 20.7546C15.9492 20.8067 15.9492 20.9108 16 20.9628C16 21.119 16 21.2751 16 21.4312C16 21.5353 16 21.5874 15.9492 21.6394L15.8983 21.9517H10.1017L10 21.7435C10 21.6915 10 21.5874 9.94915 21.5353C10 21.4312 9.94915 21.3271 9.94915 21.223Z',
          fill: '#FFFFFF',
        },
      },
    ],
  },
  'lock-check': {
    path: 'M14.6818 7.46939H13.1364V5.07398C13.1364 2.27585 11.0568 0 8.5 0C5.94325 0 3.86364 2.2759 3.86364 5.07398V7.46939H2.31818C1.04028 7.46939 0 8.60785 0 10.0064V17.463C0 18.8615 1.04028 20 2.31818 20H14.6818C15.9597 20 17 18.8615 17 17.463V10.0064C17 8.60785 15.9597 7.46939 14.6818 7.46939ZM5.40909 5.07398C5.40909 3.2093 6.79518 1.69133 8.5 1.69133C10.2048 1.69133 11.5909 3.2093 11.5909 5.07398V7.46939H5.40909V5.07398ZM7.40755 17.1724L4.12822 13.5835L5.22067 12.389L7.40651 14.7812L11.7783 9.99782L12.8707 11.1934L7.40755 17.1724Z',
    viewBox: '0 0 17 20',
    fill: true,
  },
  visa: {
    viewBox: '0 0 49 31',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M2.48652 0H46.0007C47.374 0 48.4872 1.11036 48.4872 2.48V28.52C48.4872 29.8897 47.374 31 46.0007 31H2.48652C1.11328 31 0 29.8897 0 28.52V2.48C0 1.11036 1.11328 0 2.48652 0Z',
          fill: '#3A6DB7',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M17.3145 21.3261L19.3793 9.7752H22.6819L20.6156 21.3261H17.3145ZM32.5469 10.0242C31.8927 9.79019 30.8673 9.53906 29.5871 9.53906C26.3238 9.53906 24.0251 11.1057 24.0056 13.351C23.9871 15.0108 25.6466 15.9367 26.8992 16.4892C28.1848 17.0554 28.6168 17.4164 28.6108 17.9221C28.6027 18.6963 27.5842 19.05 26.635 19.05C25.3132 19.05 24.611 18.8749 23.5264 18.4437L23.1008 18.2602L22.6373 20.8459C23.4086 21.1684 24.8351 21.4477 26.316 21.4621C29.7876 21.4621 32.0411 19.9135 32.0668 17.5157C32.0792 16.2018 31.1994 15.2018 29.294 14.3774C28.1397 13.8431 27.4328 13.4865 27.4403 12.9454C27.4403 12.4653 28.0386 11.9518 29.3315 11.9518C30.4115 11.9359 31.1939 12.1604 31.8034 12.3944L32.0993 12.5277L32.5469 10.0242ZM41.0453 9.77502H38.4934C37.7028 9.77502 37.1112 9.98078 36.7641 10.7329L31.8594 21.3185H35.3273C35.3273 21.3185 35.8943 19.8951 36.0225 19.5826C36.4016 19.5826 39.7704 19.5876 40.2521 19.5876C40.3509 19.992 40.6538 21.3185 40.6538 21.3185H43.7183L41.0453 9.77467V9.77502ZM36.9965 17.2339C37.2695 16.5684 38.3123 14.0049 38.3123 14.0049C38.2927 14.0357 38.5833 13.3362 38.7501 12.9025L38.9732 13.8984C38.9732 13.8984 39.6057 16.6557 39.7379 17.2339H36.9965ZM14.5109 9.77502L11.2777 17.6522L10.9331 16.0514C10.3312 14.2061 8.45586 12.2069 6.35938 11.206L9.31573 21.3077L12.8099 21.3039L18.0091 9.77491H14.5109',
          fill: 'white',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M8.49776 9.5393H3.22145L3.17969 9.78253C7.28454 10.7502 10.0007 13.0886 11.1284 15.898L9.98105 10.526C9.78297 9.78581 9.20856 9.56492 8.49788 9.53906',
          fill: '#F2AE14',
        },
      },
    ],
  },
  mastercard: {
    viewBox: '0 0 48 31',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M2.94727 1H45.0527C46.1834 1.00005 47 1.85166 47 2.78223V28.2178C47 29.1483 46.1834 29.9999 45.0527 30H2.94727C1.81665 29.9999 1 29.1483 1 28.2178V2.78223C1 1.85166 1.81665 1.00005 2.94727 1Z',
          fill: '#F3F3F3',
          stroke: '#F3F3F3',
          'stroke-width': '2',
        },
      },
      {
        tag: 'path',
        attrs: { d: 'M28.4706 22.9786H19.7539V7.42969H28.4706V22.9786Z', fill: '#FF5F00' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M20.3135 15.2052C20.3135 12.051 21.8013 9.24131 24.1184 7.4307C22.424 6.10668 20.2856 5.31641 17.9616 5.31641C12.4599 5.31641 8 9.74371 8 15.2052C8 20.6666 12.4599 25.0939 17.9616 25.0939C20.2856 25.0939 22.424 24.3036 24.1184 22.9796C21.8013 21.169 20.3135 18.3593 20.3135 15.2052Z',
          fill: '#EB001B',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M40.2284 15.2052C40.2284 20.6666 35.7685 25.0939 30.2668 25.0939C27.9427 25.0939 25.8044 24.3036 24.1094 22.9796C26.427 21.169 27.9148 18.3593 27.9148 15.2052C27.9148 12.051 26.427 9.24131 24.1094 7.4307C25.8044 6.10668 27.9427 5.31641 30.2668 5.31641C35.7685 5.31641 40.2284 9.74371 40.2284 15.2052Z',
          fill: '#F79E1B',
        },
      },
    ],
  },
  'delete-circle': {
    viewBox: '0 0 19 19',
    fill: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M9.38442 0C7.52836 0 5.71398 0.550386 4.17072 1.58156C2.62746 2.61273 1.42463 4.07838 0.71435 5.79315C0.00406611 7.50793 -0.181777 9.39482 0.180323 11.2152C0.542423 13.0356 1.4362 14.7078 2.74864 16.0202C4.06107 17.3326 5.73321 18.2264 7.55361 18.5885C9.37401 18.9506 11.2609 18.7648 12.9757 18.0545C14.6905 17.3442 16.1561 16.1414 17.1873 14.5981C18.2184 13.0549 18.7688 11.2405 18.7688 9.38441C18.7659 6.89641 17.7762 4.51116 16.017 2.75187C14.2577 0.99259 11.8724 0.00293516 9.38442 0ZM9.38442 17.0626C7.86582 17.0626 6.38133 16.6123 5.11866 15.7686C3.856 14.9249 2.87187 13.7257 2.29073 12.3227C1.70958 10.9197 1.55753 9.3759 1.85379 7.88648C2.15006 6.39706 2.88133 5.02895 3.95514 3.95514C5.02895 2.88133 6.39707 2.15005 7.88648 1.85379C9.3759 1.55753 10.9197 1.70958 12.3227 2.29072C13.7257 2.87186 14.9249 3.85599 15.7686 5.11866C16.6123 6.38132 17.0626 7.86582 17.0626 9.38441C17.0601 11.42 16.2503 13.3715 14.811 14.8109C13.3716 16.2503 11.42 17.0601 9.38442 17.0626Z',
        },
      },
      {
        tag: 'rect',
        attrs: {
          x: '6.71484',
          y: '5.51562',
          width: '9.24416',
          height: '1.69966',
          rx: '0.84983',
          transform: 'rotate(45 6.71484 5.51562)',
        },
      },
      {
        tag: 'rect',
        attrs: {
          x: '13.2539',
          y: '6.71875',
          width: '9.24416',
          height: '1.69966',
          rx: '0.84983',
          transform: 'rotate(135 13.2539 6.71875)',
        },
      },
    ],
  },
  envelope: {
    path: 'M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z',
    viewBox: '0 0 24 24',
    fill: true,
  },
  globe: {
    path: 'M21.721 12.752a9.711 9.711 0 00-.945-5.003 12.754 12.754 0 01-4.339 2.708 18.991 18.991 0 01-.214 4.772 17.165 17.165 0 005.498-2.477zM14.634 15.55a17.324 17.324 0 00.332-4.647c-.952.227-1.945.347-2.966.347-1.021 0-2.014-.12-2.966-.347a17.515 17.515 0 00.332 4.647 17.385 17.385 0 005.268 0zM9.772 17.119a18.963 18.963 0 004.456 0A17.182 17.182 0 0112 21.724a17.18 17.18 0 01-2.228-4.605zM7.777 15.23a18.87 18.87 0 01-.214-4.774 12.753 12.753 0 01-4.34-2.708 9.711 9.711 0 00-.944 5.004 17.165 17.165 0 005.498 2.477zM21.356 14.752a9.765 9.765 0 01-7.478 6.817 18.64 18.64 0 001.988-4.718 18.627 18.627 0 005.49-2.098zM2.644 14.752c1.682.971 3.53 1.688 5.49 2.099a18.64 18.64 0 001.988 4.718 9.765 9.765 0 01-7.478-6.816zM13.878 2.43a9.755 9.755 0 016.116 3.986 11.267 11.267 0 01-3.746 2.504 18.63 18.63 0 00-2.37-6.49zM12 2.276a17.152 17.152 0 012.805 7.121c-.897.23-1.837.353-2.805.353-.968 0-1.908-.122-2.805-.354A17.151 17.151 0 0112 2.276zM10.122 2.43a18.629 18.629 0 00-2.37 6.49 11.266 11.266 0 01-3.746-2.504 9.754 9.754 0 016.116-3.985z',
    viewBox: '0 0 24 24',
    fill: true,
    fillRule: 'evenodd',
  },
  database: {
    path: 'M12 3.75c-4.97 0-9 1.343-9 3v2.5c0 1.657 4.03 3 9 3s9-1.343 9-3v-2.5c0-1.657-4.03-3-9-3zm-9 8v3c0 1.657 4.03 3 9 3s9-1.343 9-3v-3c-1.835 1.14-5.253 1.75-9 1.75s-7.165-.61-9-1.75zm0 5.5v3c0 1.657 4.03 3 9 3s9-1.343 9-3v-3c-1.835 1.14-5.253 1.75-9 1.75s-7.165-.61-9-1.75z',
    viewBox: '0 0 24 24',
    fill: true,
  },
  crown: {
    path: 'M2 20h20v2H2v-2zm1.15-4.05L2 4l5.5 4L12 2l4.5 6L22 4l-1.15 11.95H3.15z',
    viewBox: '0 0 24 24',
    fill: true,
  },
  briefcase: {
    path: 'M1 7.5a2 2 0 012-2h3.5V3.5a2 2 0 012-2h7a2 2 0 012 2v2H21a2 2 0 012 2V20a2 2 0 01-2 2H3a2 2 0 01-2-2V7.5zm7.5-4h7v2h-7v-2z',
    viewBox: '0 0 24 24',
    fill: true,
    fillRule: 'evenodd',
  },
  'eye-open': {
    path: 'M17.7469 15.4149C17.9855 14.8742 18.1188 14.2724 18.1188 14.0016C18.1188 11.6544 16.2952 9.7513 14.046 9.7513C11.7969 9.7513 9.97332 11.6544 9.97332 14.0016C9.97332 16.3487 12.0097 17.8886 14.046 17.8886C15.3486 17.8886 16.508 17.2515 17.2517 16.2595C17.4466 16.0001 17.6137 15.7168 17.7469 15.4149ZM14.046 15.7635C14.5551 15.7635 15.0205 15.5684 15.3784 15.2457C15.81 14.8566 16 14.2807 16 14.0016C16 12.828 15.1716 11.8764 14.046 11.8764C12.9205 11.8764 12 12.8264 12 14C12 14.8104 12.9205 15.7635 14.046 15.7635Z M1.09212 14.2724C1.07621 14.2527 1.10803 14.2931 1.09212 14.2724C0.96764 14.1021 0.970773 13.8996 1.09268 13.7273C1.10161 13.7147 1.11071 13.7016 1.11993 13.6882C4.781 8.34319 9.32105 5.5 14.0142 5.5C18.7025 5.5 23.2385 8.33554 26.8956 13.6698C26.965 13.771 27 13.875 27 13.9995C27 14.1301 26.9593 14.2399 26.8863 14.3461C23.2302 19.6702 18.6982 22.5 14.0142 22.5C9.30912 22.5 4.75717 19.6433 1.09212 14.2724ZM3.93909 13.3525C3.6381 13.7267 3.6381 14.2722 3.93908 14.6465C7.07417 18.5443 10.6042 20.3749 14.0142 20.3749C17.4243 20.3749 20.9543 18.5443 24.0894 14.6465C24.3904 14.2722 24.3904 13.7267 24.0894 13.3525C20.9543 9.45475 17.4243 7.62513 14.0142 7.62513C10.6042 7.62513 7.07417 9.45475 3.93909 13.3525Z',
    viewBox: '0 0 28 28',
    fill: true,
    fillRule: 'evenodd',
  },
  cpanel: {
    viewBox: '0 0 16 16',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '8', cy: '8', r: '8', fill: '#E55E33' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M14.9206 6.62979C14.5499 5.04681 13.1401 4 11.545 4H9.09602C8.62609 4 8.26867 4.28723 8.14953 4.69574L5.83956 13L7.47441 12.9936C7.92449 12.9936 8.28853 12.7064 8.41428 12.3106L10.1219 6.20213H11.4325C11.9884 6.20213 12.5047 6.55957 12.6437 7.14681C12.8092 7.84255 12.2532 8.58936 11.4325 8.58936H10.7507C10.2874 8.58936 9.92338 8.88298 9.80424 9.27872L9.38063 10.7979H11.4987C13.9609 10.7979 15.3574 8.48723 14.9206 6.62979ZM4.56213 8.41702H5.79323C6.24993 8.41702 6.61396 8.13617 6.73972 7.72128L7.15009 6.21489H4.50256C2.98685 6.21489 1.5969 7.11489 1.11372 8.8383C0.537884 10.9957 2.22568 13 4.44299 13H5.26372L5.73366 11.334C5.81309 11.0532 5.58805 10.8043 5.31006 10.8043H4.56213C2.92728 10.8043 2.91404 8.41702 4.56213 8.41702Z',
          fill: 'white',
        },
      },
    ],
  },
  'cpanel-suspended': {
    viewBox: '0 0 18 17',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '8', cy: '8', r: '8', fill: '#D2D2D2' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M14.9206 6.62979C14.5499 5.04681 13.1401 4 11.545 4H9.09602C8.62609 4 8.26867 4.28723 8.14953 4.69574L5.83956 13L7.47441 12.9936C7.92449 12.9936 8.28853 12.7064 8.41428 12.3106L10.1219 6.20213H11.4325C11.9884 6.20213 12.5047 6.55957 12.6437 7.14681C12.8092 7.84255 12.2532 8.58936 11.4325 8.58936H10.7507C10.2874 8.58936 9.92338 8.88298 9.80424 9.27872L9.38063 10.7979H11.4987C13.9609 10.7979 15.3574 8.48723 14.9206 6.62979ZM4.56213 8.41702H5.79323C6.24993 8.41702 6.61396 8.13617 6.73972 7.72128L7.15009 6.21489H4.50256C2.98685 6.21489 1.5969 7.11489 1.11372 8.8383C0.537884 10.9957 2.22568 13 4.44299 13H5.26372L5.73366 11.334C5.81309 11.0532 5.58805 10.8043 5.31006 10.8043H4.56213C2.92728 10.8043 2.91404 8.41702 4.56213 8.41702Z',
          fill: 'white',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M15.2961 13.5L16.8324 11.9722C17.0559 11.75 17.0559 11.4167 16.8324 11.1944C16.7486 11.0833 16.6089 11 16.4693 11C16.3296 11 16.1899 11.0556 16.0782 11.1667L14.514 12.7222L12.9777 11.1944C12.7542 10.9722 12.3911 10.9722 12.1955 11.1944C12.0838 11.2778 12 11.4167 12 11.5833C12 11.75 12.0559 11.8611 12.1676 11.9722L13.7039 13.5L12.1676 15.0278C12.0838 15.1389 12 15.2778 12 15.4444C12 15.5833 12.0559 15.7222 12.1676 15.8333C12.2793 15.9444 12.419 16 12.5587 16C12.6983 16 12.838 15.9444 12.9497 15.8333L14.486 14.3056L16.0223 15.8333C16.2458 16.0556 16.6089 16.0556 16.8045 15.8333C17.0279 15.6111 17.0279 15.25 16.8045 15.0556L15.2961 13.5Z',
          fill: '#BD505A',
          stroke: '#BD505A',
        },
      },
    ],
  },
  wordpress: {
    viewBox: '0 0 16 16',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M7.99998 0.48C9.50398 0.48 10.968 0.928003 12.208 1.768C13.416 2.584 14.368 3.736 14.936 5.08C15.928 7.42398 15.664 10.104 14.24 12.208C13.424 13.416 12.272 14.368 10.928 14.936C8.58398 15.928 5.90398 15.664 3.8 14.24C2.592 13.424 1.64 12.272 1.072 10.928C0.08 8.58398 0.344 5.90398 1.768 3.8C2.584 2.592 3.736 1.64 5.08 1.072C5.99998 0.680003 6.99998 0.48 7.99998 0.48ZM7.99998 0C3.584 0 0 3.584 0 7.99998C0 12.416 3.584 16 7.99998 16C12.416 16 16 12.416 16 7.99998C16 3.584 12.416 0 7.99998 0Z',
          fill: '#206B8D',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M1.33984 8.00348C1.33984 10.5555 2.79584 12.8915 5.09984 14.0035L1.91584 5.29151C1.53184 6.14748 1.33984 7.06748 1.33984 8.00348ZM12.5078 7.66748C12.5078 6.84348 12.2118 6.27548 11.9558 5.82751C11.6998 5.37951 11.2998 4.81151 11.2998 4.26751C11.2998 3.72351 11.7638 3.08351 12.4198 3.08351H12.5078C9.79584 0.595509 5.57984 0.779508 3.09184 3.49951C2.85184 3.76351 2.62784 4.04351 2.43584 4.33951H2.86784C3.56384 4.33951 4.64384 4.25151 4.64384 4.25151C5.00384 4.22751 5.04384 4.75551 4.68384 4.80351C4.68384 4.80351 4.32384 4.84351 3.92384 4.86751L6.34784 12.0915L7.80384 7.71548L6.76384 4.86751C6.40384 4.84351 6.06784 4.80351 6.06784 4.80351C5.70784 4.77951 5.74784 4.23551 6.10784 4.25151C6.10784 4.25151 7.21184 4.33951 7.85984 4.33951C8.50784 4.33951 9.63584 4.25151 9.63584 4.25151C9.99584 4.22751 10.0358 4.75551 9.67583 4.80351C9.67583 4.80351 9.31584 4.84351 8.91584 4.86751L11.3238 12.0355L12.0118 9.85948C12.3158 8.90748 12.4998 8.23548 12.4998 7.65948L12.5078 7.66748ZM8.11584 8.57948L6.11584 14.3955C7.45984 14.7875 8.89184 14.7555 10.2118 14.2915L10.1638 14.2035L8.11584 8.57948ZM13.8518 4.80351C13.8838 5.02751 13.8998 5.25951 13.8998 5.49151C13.8998 6.17148 13.7718 6.93148 13.3958 7.87548L11.3558 13.7635C14.4758 11.9475 15.5878 7.97148 13.8518 4.80351Z',
          fill: '#206B8D',
        },
      },
    ],
  },
  'wordpress-suspended': {
    viewBox: '0 0 17 16',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M8.20703 12.6387C7.29891 12.8731 6.34858 12.8951 5.42676 12.708L6.78906 8.74512L8.20703 12.6387ZM2.78613 11.4658C1.35788 10.3108 0.500128 8.55546 0.5 6.67188C0.5 6.27975 0.537751 5.89095 0.610352 5.50977L2.78613 11.4658ZM12.7891 5.95215C13.005 7.8031 12.3661 9.67958 11.0342 11.0176L12.5254 6.7168C12.6312 6.4502 12.7176 6.19572 12.7891 5.95215ZM4.0332 3.48828C4.05803 3.55266 4.08891 3.61685 4.13379 3.67578C4.26193 3.84392 4.45328 3.94689 4.66992 3.96777C4.67072 3.96787 4.6724 3.96858 4.67383 3.96875C4.67661 3.96908 4.68057 3.96915 4.68555 3.96973C4.69599 3.97093 4.7114 3.97249 4.73047 3.97461C4.769 3.97889 4.82429 3.9855 4.89062 3.99219C4.94176 3.99734 5.00046 4.00016 5.06348 4.00586L5.93359 6.39062L5.00586 9.18066L3.26074 3.98242C3.28852 3.97969 3.31296 3.97765 3.33301 3.97559C3.35347 3.97348 3.36963 3.97191 3.38086 3.9707C3.3865 3.9701 3.39149 3.96908 3.39453 3.96875H3.39941L3.41016 3.9668C3.73355 3.92364 3.93889 3.72263 4.0332 3.48828ZM2.13379 2.49121C4.23641 0.206234 7.66017 -0.129079 10.1455 1.57715C10.0534 1.64821 9.9698 1.72711 9.89648 1.81152C9.62789 2.12081 9.45996 2.53086 9.45996 2.93555C9.45998 3.32258 9.60013 3.68342 9.74414 3.97363C9.88382 4.25512 10.0731 4.5532 10.1816 4.74316C10.4034 5.13129 10.6221 5.55948 10.6602 6.15332V6.32715C10.6602 6.81301 10.5027 7.41225 10.1953 8.375V8.37695L9.96875 9.08984L8.25293 3.98242C8.28063 3.97969 8.3052 3.97765 8.3252 3.97559C8.34568 3.97347 8.36185 3.97191 8.37305 3.9707C8.37844 3.97012 8.38272 3.96908 8.38574 3.96875H8.3916L8.40234 3.9668C8.88536 3.90223 9.11115 3.48748 9.08496 3.13184C9.07204 2.95736 8.99965 2.77149 8.85156 2.62891C8.69516 2.47852 8.48435 2.40612 8.2627 2.4209H8.25391C8.25189 2.42105 8.24897 2.42158 8.24512 2.42188C8.23696 2.4225 8.22472 2.42363 8.20898 2.4248C8.17745 2.42717 8.13143 2.4305 8.07422 2.43457C7.95971 2.44272 7.80031 2.45398 7.62109 2.46484C7.25895 2.48679 6.82718 2.50781 6.51953 2.50781C6.2121 2.50779 5.78739 2.48677 5.43164 2.46484C5.25565 2.454 5.09953 2.44272 4.9873 2.43457C4.9312 2.4305 4.88537 2.42717 4.85449 2.4248C4.83922 2.42364 4.82726 2.42249 4.81934 2.42188C4.81544 2.42157 4.8125 2.42105 4.81055 2.4209H4.80762L4.79883 2.41992H4.79004C4.56328 2.40989 4.35494 2.49156 4.2041 2.64355C4.12839 2.7199 4.07409 2.80801 4.03711 2.89941C3.9989 2.80235 3.94211 2.70854 3.85938 2.62891C3.70297 2.47852 3.49216 2.40612 3.27051 2.4209H3.26074C3.25882 2.42105 3.25583 2.42157 3.25195 2.42188C3.24423 2.42249 3.23272 2.42364 3.21777 2.4248C3.18756 2.42716 3.14308 2.43051 3.08789 2.43457C2.97731 2.44271 2.82249 2.454 2.64648 2.46484C2.48586 2.47474 2.30936 2.4835 2.13379 2.49121Z',
          fill: '#D2D2D2',
          stroke: '#D2D2D2',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M13.9562 12.168L15.4926 10.6402C15.716 10.418 15.716 10.0846 15.4926 9.86241C15.4088 9.7513 15.2691 9.66797 15.1294 9.66797C14.9898 9.66797 14.8501 9.72352 14.7384 9.83464L13.1741 11.3902L11.6378 9.86241C11.4143 9.64019 11.0512 9.64019 10.8557 9.86241C10.744 9.94575 10.6602 10.0846 10.6602 10.2513C10.6602 10.418 10.716 10.5291 10.8278 10.6402L12.3641 12.168L10.8278 13.6957C10.744 13.8069 10.6602 13.9457 10.6602 14.1124C10.6602 14.2513 10.716 14.3902 10.8278 14.5013C10.9395 14.6124 11.0792 14.668 11.2188 14.668C11.3585 14.668 11.4981 14.6124 11.6099 14.5013L13.1462 12.9735L14.6825 14.5013C14.906 14.7235 15.2691 14.7235 15.4646 14.5013C15.6881 14.2791 15.6881 13.918 15.4646 13.7235L13.9562 12.168Z',
          fill: '#BD505A',
          stroke: '#BD505A',
        },
      },
    ],
  },
  'question-red': {
    viewBox: '0 0 14 14',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14ZM4.144 5.0638V5.1352C4.144 5.22803 4.18087 5.31705 4.24651 5.38269C4.31215 5.44833 4.40117 5.4852 4.494 5.4852H5.1919C5.23777 5.4852 5.28319 5.47616 5.32557 5.45861C5.36795 5.44106 5.40646 5.41533 5.43889 5.38289C5.47133 5.35046 5.49706 5.31195 5.51461 5.26957C5.53216 5.22719 5.5412 5.18177 5.5412 5.1359C5.5412 4.2329 6.2398 3.7506 7.1792 3.7506C8.0948 3.7506 8.6968 4.2329 8.6968 4.9196C8.6968 5.5692 8.3594 5.8709 7.5166 6.2566L7.2758 6.3644C6.5653 6.678 6.3 7.147 6.3 7.9667V8.05C6.3 8.14283 6.33687 8.23185 6.40251 8.29749C6.46815 8.36313 6.55717 8.4 6.65 8.4H7.3479C7.39377 8.4 7.43919 8.39097 7.48157 8.37341C7.52395 8.35586 7.56246 8.33013 7.59489 8.29769C7.62733 8.26526 7.65306 8.22675 7.67061 8.18437C7.68817 8.14199 7.6972 8.09657 7.6972 8.0507C7.6972 7.6895 7.7938 7.5446 8.0822 7.4123L8.3237 7.3038C9.3352 6.846 10.094 6.244 10.094 4.9308V4.8587C10.094 3.4741 8.89 2.45 7.1792 2.45C5.4446 2.45 4.144 3.4496 4.144 5.0638ZM5.95 10.4937C5.95 11.1006 6.4113 11.55 6.9937 11.55C7.5887 11.55 8.05 11.1006 8.05 10.4937C8.05 9.8868 7.5887 9.45 6.9937 9.45C6.4113 9.45 5.95 9.8868 5.95 10.4937Z',
          fill: '#D53E3E',
        },
      },
    ],
  },
  'question-white': {
    viewBox: '0 0 11 11',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M5.45436 10.9087C2.44192 10.9087 0 8.46681 0 5.45436C0 2.44192 2.44192 0 5.45436 0C8.46681 0 10.9087 2.44192 10.9087 5.45436C10.9087 8.46681 8.46681 10.9087 5.45436 10.9087ZM3.22898 3.94569V4.00132C3.22898 4.07365 3.25772 4.14302 3.30886 4.19416C3.36001 4.24531 3.42937 4.27404 3.5017 4.27404H4.0455C4.08124 4.27404 4.11664 4.267 4.14966 4.25332C4.18268 4.23964 4.21268 4.2196 4.23796 4.19432C4.26323 4.16905 4.28328 4.13904 4.29696 4.10602C4.31064 4.073 4.31768 4.03761 4.31768 4.00187C4.31768 3.29825 4.86202 2.92245 5.594 2.92245C6.30743 2.92245 6.7765 3.29825 6.7765 3.83333C6.7765 4.33949 6.5136 4.57458 5.8569 4.87511L5.66927 4.95911C5.11565 5.20346 4.90893 5.56891 4.90893 6.20761V6.27252C4.90893 6.34485 4.93766 6.41422 4.98881 6.46536C5.03995 6.51651 5.10932 6.54524 5.18165 6.54524H5.72545C5.76119 6.54524 5.79658 6.5382 5.8296 6.52452C5.86262 6.51084 5.89263 6.49079 5.9179 6.46552C5.94318 6.44025 5.96322 6.41024 5.9769 6.37722C5.99058 6.3442 5.99762 6.30881 5.99762 6.27307C5.99762 5.99162 6.07289 5.87871 6.29761 5.77563L6.48579 5.69108C7.27394 5.33437 7.86519 4.86529 7.86519 3.84205V3.78587C7.86519 2.707 6.92704 1.90903 5.594 1.90903C4.2424 1.90903 3.22898 2.68791 3.22898 3.94569ZM4.63621 8.17664C4.63621 8.64953 4.99565 8.9997 5.44946 8.9997C5.91308 8.9997 6.27252 8.64953 6.27252 8.17664C6.27252 7.70374 5.91308 7.36339 5.44946 7.36339C4.99565 7.36339 4.63621 7.70374 4.63621 8.17664Z',
          fill: 'white',
        },
      },
    ],
  },
  pencil: {
    viewBox: '0 0 11 11',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M5.16635 3L0 8.16636V11H2.83364L8 5.83364L5.16635 3Z',
          fill: '#B5B5B5',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M6 1.82181L9.17818 5L10.3418 3.83642C10.7632 3.41496 11 2.84334 11 2.24732C11 1.00616 9.99387 0 8.7527 0C8.15663 0 7.58503 0.236771 7.16361 0.658225L6 1.82181Z',
          fill: '#B5B5B5',
        },
      },
    ],
  },
  cog: {
    viewBox: '0 0 14 15',
    fill: true,
    fillRule: 'evenodd',
    path: 'M8.31069 0.11418C8.03783 3.35276e-08 7.69189 0 6.99999 0C6.3081 0 5.96215 3.35276e-08 5.6893 0.11418C5.32545 0.266423 5.03638 0.558435 4.88567 0.925973C4.81687 1.09376 4.78995 1.28887 4.77941 1.57349C4.76393 1.99176 4.55159 2.37892 4.19275 2.5882C3.83392 2.79747 3.39583 2.78965 3.02951 2.59407C2.78023 2.46097 2.59949 2.38697 2.42125 2.36327C2.0308 2.31134 1.63592 2.41822 1.32348 2.6604C1.08915 2.84204 0.916179 3.14468 0.57024 3.74995C0.2243 4.35523 0.0513341 4.65786 0.0127825 4.95368C-0.0386269 5.3481 0.0671835 5.74699 0.30693 6.06262C0.416358 6.2067 0.570144 6.32775 0.808829 6.47925C1.15972 6.702 1.3855 7.08143 1.38547 7.5C1.38545 7.91857 1.15968 8.29792 0.808829 8.5206C0.570107 8.67217 0.416292 8.7933 0.306856 8.93738C0.0671099 9.25298 -0.0386931 9.65182 0.0127089 10.0463C0.0512604 10.3421 0.224234 10.6447 0.570166 11.25C0.916106 11.8553 1.08908 12.1579 1.32341 12.3395C1.63585 12.5817 2.03073 12.6886 2.42118 12.6367C2.59941 12.613 2.78014 12.539 3.0294 12.4059C3.39575 12.2103 3.83386 12.2025 4.19271 12.4117C4.55157 12.6211 4.76392 13.0082 4.77941 13.4266C4.78995 13.7111 4.81687 13.9063 4.88567 14.074C5.03638 14.4416 5.32545 14.7336 5.6893 14.8858C5.96215 15 6.3081 15 6.99999 15C7.69189 15 8.03783 15 8.31069 14.8858C8.67454 14.7336 8.9636 14.4416 9.11428 14.074C9.1831 13.9063 9.21007 13.7111 9.22061 13.4265C9.23608 13.0082 9.44837 12.6211 9.80721 12.4117C10.1661 12.2024 10.6042 12.2103 10.9705 12.4059C11.2198 12.539 11.4005 12.6129 11.5787 12.6366C11.9692 12.6886 12.364 12.5817 12.6765 12.3395C12.9109 12.1579 13.0838 11.8553 13.4297 11.2499C13.7757 10.6447 13.9486 10.3421 13.9872 10.0463C14.0386 9.65182 13.9328 9.2529 13.6931 8.9373C13.5836 8.79323 13.4298 8.6721 13.1911 8.5206C12.8403 8.29792 12.6145 7.9185 12.6145 7.49992C12.6145 7.08135 12.8403 6.70208 13.1911 6.4794C13.4299 6.32783 13.5837 6.20677 13.6932 6.06262C13.9329 5.74705 14.0387 5.34815 13.9873 4.95373C13.9487 4.65791 13.7758 4.35527 13.4298 3.75C13.0839 3.14473 12.9109 2.84209 12.6765 2.66045C12.3641 2.41827 11.9692 2.31139 11.5788 2.36332C11.4006 2.38702 11.2198 2.46102 10.9706 2.5941C10.6043 2.78969 10.1661 2.79752 9.80728 2.58822C9.44844 2.37893 9.23608 1.99174 9.22054 1.57345C9.21 1.28886 9.1831 1.09375 9.11428 0.925973C8.9636 0.558435 8.67454 0.266423 8.31069 0.11418ZM6.99999 9.75C8.23015 9.75 9.22732 8.74267 9.22732 7.5C9.22732 6.25733 8.23015 5.25 6.99999 5.25C5.76983 5.25 4.77264 6.25733 4.77264 7.5C4.77264 8.74267 5.76983 9.75 6.99999 9.75Z',
  },
  plus: {
    viewBox: '0 0 11 11',
    fill: true,
    fillRule: 'evenodd',
    path: 'M9.625 4.125H6.875V1.375C6.875 0.615312 6.25934 0 5.5 0C4.74066 0 4.125 0.615312 4.125 1.375V4.125H1.375C0.615656 4.125 0 4.74031 0 5.5C0 6.25969 0.615656 6.875 1.375 6.875H4.125V9.625C4.125 10.3847 4.74066 11 5.5 11C6.25934 11 6.875 10.3847 6.875 9.625V6.875H9.625C10.3843 6.875 11 6.25969 11 5.5C11 4.74031 10.3843 4.125 9.625 4.125Z',
  },
  minus: {
    viewBox: '0 0 12 4',
    elements: [
      {
        tag: 'rect',
        attrs: { y: '0.5', width: '12', height: '3', rx: '1.5', fill: 'currentColor' },
      },
    ],
  },
  'success-circle': {
    viewBox: '0 0 26 26',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '13', cy: '13', r: '7', fill: '#255D4E' },
      },
      {
        tag: 'circle',
        attrs: { cx: '13', cy: '13', r: '12.5', stroke: '#255D4E', fill: 'none' },
      },
    ],
  },
  'check-circle-green': {
    viewBox: '0 0 20 20',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '10', cy: '10', r: '10', fill: '#4e9758' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M4.43507 9.4757C4.14502 9.75192 4 10.0895 4 10.4885C4 10.8875 4.14502 11.2251 4.43507 11.5013L7.4806 14.5627C7.75538 14.8542 8.09123 15 8.48814 15C8.88505 15 9.2209 14.8542 9.49568 14.5627L15.5638 8.46292C15.8692 8.17136 16.0142 7.83376 15.9989 7.45013C15.9836 7.0665 15.8386 6.7289 15.5638 6.43734C15.2891 6.14578 14.9532 6 14.5563 6C14.1594 6 13.8235 6.14578 13.5488 6.43734L8.48814 11.5013L6.45016 9.4757C6.16011 9.19949 5.82426 9.06138 5.44262 9.06138C5.06097 9.06138 4.72512 9.19949 4.43507 9.4757Z',
          fill: '#ffffff',
        },
      },
    ],
  },
  clock: {
    viewBox: '0 0 24 24',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '12', cy: '12', r: '9', fill: 'none', stroke: '#b45309', 'stroke-width': '2' },
      },
      {
        tag: 'path',
        attrs: { d: 'M12 7V12L16 14', fill: 'none', stroke: '#b45309', 'stroke-width': '2' },
      },
    ],
  },
  'error-circle': {
    viewBox: '0 0 26 26',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M23.4 13C23.4 7.25623 18.7438 2.59998 13 2.59998C7.25624 2.59998 2.59998 7.25624 2.59998 13C2.59998 18.7438 7.25623 23.4 13 23.4C18.7438 23.4 23.4 18.7438 23.4 13ZM26 13C26 20.1798 20.1798 26 13 26C5.8203 26 0 20.1798 0 13C0 5.82029 5.82029 0 13 0C20.1798 0 26 5.8203 26 13Z',
          fill: '#DB2863',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M11.0039 13.8572V8.14285C11.0039 7.51166 11.8993 7 13.0039 7C14.1085 7 15.0039 7.51166 15.0039 8.14285V13.8572C15.0039 14.4883 14.1085 15 13.0039 15C11.8993 15 11.0039 14.4883 11.0039 13.8572Z',
          fill: '#DB2863',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M11.0039 18.0072V17.9849C11.0039 16.8865 11.8993 15.9961 13.0039 15.9961C14.1085 15.9961 15.0039 16.8865 15.0039 17.9849V18.0072C15.0039 19.1056 14.1085 19.9961 13.0039 19.9961C11.8993 19.9961 11.0039 19.1056 11.0039 18.0072Z',
          fill: '#DB2863',
        },
      },
    ],
  },
  'check-circle-grey': {
    viewBox: '0 0 20 20',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '10', cy: '10', r: '10', fill: '#E8EBED' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M4.43507 9.4757C4.14502 9.75192 4 10.0895 4 10.4885C4 10.8875 4.14502 11.2251 4.43507 11.5013L7.4806 14.5627C7.75538 14.8542 8.09123 15 8.48814 15C8.88505 15 9.2209 14.8542 9.49568 14.5627L15.5638 8.46292C15.8692 8.17136 16.0142 7.83376 15.9989 7.45013C15.9836 7.0665 15.8386 6.7289 15.5638 6.43734C15.2891 6.14578 14.9532 6 14.5563 6C14.1594 6 13.8235 6.14578 13.5488 6.43734L8.48814 11.5013L6.45016 9.4757C6.16011 9.19949 5.82426 9.06138 5.44262 9.06138C5.06097 9.06138 4.72512 9.19949 4.43507 9.4757Z',
          fill: '#2CAA9A',
        },
      },
    ],
  },
  'arrow-right-green': {
    viewBox: '0 0 13 14',
    multiColor: true,
    elements: [
      {
        tag: 'rect',
        attrs: { width: '13', height: '14', rx: '3', fill: '#2CAA9A' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M9.5 7.00001C9.5 7.13261 9.44147 7.25981 9.33728 7.35356L5.44838 10.8536C5.23143 11.0488 4.87965 11.0488 4.66271 10.8536C4.44576 10.6583 4.44576 10.3417 4.66271 10.1465L8.15877 7.00001L4.66271 3.85356C4.44576 3.65829 4.44576 3.34171 4.66271 3.14645C4.87965 2.95118 5.23143 2.95118 5.44838 3.14645L9.33728 6.64646C9.44147 6.74021 9.5 6.86741 9.5 7.00001Z',
          fill: 'white',
          stroke: 'white',
          fillRule: 'evenodd',
          clipRule: 'evenodd',
        },
      },
    ],
  },
  'eye-closed': {
    path: 'M22.6928 1.55018C22.3102 1.32626 21.8209 1.45915 21.6 1.84698L19.1533 6.14375C17.4864 5.36351 15.7609 4.96457 14.0142 4.96457C9.32104 4.96457 4.781 7.84644 1.11993 13.2641L1.10541 13.2854L1.09271 13.3038C0.970762 13.4784 0.967649 13.6837 1.0921 13.8563C3.79364 17.8691 6.97705 20.4972 10.3484 21.6018L8.39935 25.0222C8.1784 25.4101 8.30951 25.906 8.69214 26.1299L9.03857 26.3326C9.4212 26.5565 9.91046 26.4237 10.1314 26.0358L23.332 2.86058C23.553 2.47275 23.4219 1.97684 23.0392 1.75291L22.6928 1.55018ZM18.092 8.00705C16.7353 7.40974 15.3654 7.1186 14.0142 7.1186C10.6042 7.1186 7.07416 8.97311 3.93908 12.9239C3.63812 13.3032 3.63812 13.8561 3.93908 14.2354C6.28912 17.197 8.86102 18.9811 11.438 19.689L12.7855 17.3232C11.2462 16.8322 9.97333 15.4627 9.97333 13.5818C9.97333 11.2026 11.7969 9.27368 14.046 9.27368C15.0842 9.27368 16.0317 9.68468 16.7511 10.3612L18.092 8.00705ZM15.639 12.3137C15.2926 11.7767 14.7231 11.4277 14.046 11.4277C12.9205 11.4277 12 12.3906 12 13.5802C12 14.3664 12.8432 15.2851 13.9024 15.3624L15.639 12.3137Z M14.6873 22.1761C19.1311 21.9148 23.4056 19.0687 26.8864 13.931C26.9593 13.8234 27 13.7121 27 13.5797C27 13.4535 26.965 13.3481 26.8956 13.2455C25.5579 11.2677 24.1025 9.62885 22.5652 8.34557L21.506 10.2052C22.3887 10.9653 23.2531 11.87 24.0894 12.9239C24.3904 13.3032 24.3904 13.8561 24.0894 14.2354C21.5676 17.4135 18.7903 19.2357 16.0254 19.827L14.6873 22.1761Z',
    viewBox: '0 0 28 28',
    fill: true,
    fillRule: 'evenodd',
  },
  linux: {
    viewBox: '0 0 216 230',
    multiColor: true,
    elements: [
      // Body (from original Tux)
      {
        tag: 'path',
        attrs: {
          d: 'M107 0C101 0 95 1 89 4C83 7 79 12 76 18C73 24 72 30 71 37C71 50 72 64 73 76C73 80 73 82 73 86C72 94 63 100 59 107C55 115 53 124 50 133C47 141 43 148 40 156C36 167 35 179 38 190C40 199 44 207 49 214C48 215 47 217 46 219C44 223 40 227 39 232C38 235 38 237 39 240C39 242 41 245 43 246C44 247 46 248 48 248C49 249 51 249 53 249C59 249 65 247 71 245C75 244 78 243 82 242C96 240 110 241 122 242C126 243 130 244 134 245C141 247 147 249 153 249C155 249 157 249 158 249C160 248 161 248 163 247C165 245 166 243 167 240C167 237 167 235 166 232C165 227 162 223 159 219C158 217 157 215 156 214C164 205 170 195 174 184C178 171 179 158 177 146C175 133 171 120 166 109C159 94 153 89 149 76C145 62 149 45 146 33C144 28 143 24 140 21C138 16 134 12 130 9C123 5 115 2 107 0Z',
          fill: '#020204',
        },
      },
      // Feet
      {
        tag: 'path',
        attrs: {
          d: 'M42 198C46 195 52 201 58 208C64 218 70 228 75 236C78 240 82 248 80 254C77 260 66 259 56 256C46 253 32 250 20 246C15 244 12 240 14 236C16 231 20 225 21 220C22 212 20 204 23 198C25 194 36 195 42 198Z',
          fill: '#E8A000',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M155 198C159 195 164 197 172 202C180 207 190 215 197 223C200 227 204 233 202 237C199 242 190 244 179 245C168 246 155 249 145 250C140 251 136 248 137 244C138 238 142 232 143 226C144 218 143 208 147 202C149 198 155 198 155 198Z',
          fill: '#E8A000',
        },
      },
      // White belly
      {
        tag: 'path',
        attrs: {
          d: 'M83 74C81 77 80 81 80 85C80 93 82 100 78 108C74 116 64 123 62 133C60 143 64 155 70 165C76 175 84 183 95 188C106 193 119 190 129 184C135 180 140 174 144 167C149 159 150 150 151 141C153 126 152 110 145 96C143 91 139 87 136 82C134 80 132 77 129 75C127 74 124 73 121 73C117 73 113 73 109 73C106 73 103 72 100 73C98 73 97 74 95 74C94 75 92 76 91 76C89 75 86 74 85 74C84 74 83 74 83 74Z',
          fill: '#FDFDFB',
        },
      },
      // Left eye white
      {
        tag: 'ellipse',
        attrs: { cx: '87', cy: '51', rx: '10', ry: '15', fill: '#FEFEFE' },
      },
      // Right eye white
      {
        tag: 'ellipse',
        attrs: { cx: '118', cy: '51', rx: '13', ry: '15', fill: '#FEFEFE' },
      },
      // Left pupil
      {
        tag: 'ellipse',
        attrs: { cx: '87', cy: '53', rx: '5', ry: '7', fill: '#020204' },
      },
      // Right pupil
      {
        tag: 'ellipse',
        attrs: { cx: '120', cy: '53', rx: '6', ry: '7', fill: '#020204' },
      },
      // Upper beak
      {
        tag: 'path',
        attrs: {
          d: 'M84 64C82 65 80 67 78 70C77 72 77 74 78 75C78 76 80 76 82 77C84 78 87 79 90 80C94 82 98 82 103 82C107 82 112 81 116 80C119 79 123 77 126 75C127 74 128 73 127 72C126 70 124 68 121 66C118 64 114 63 110 62C106 61 101 62 97 63C93 64 89 65 86 65C85 65 85 64 84 64Z',
          fill: '#E8B00B',
        },
      },
      // Lower beak
      {
        tag: 'path',
        attrs: {
          d: 'M77 75C78 77 80 79 83 81C86 83 90 85 95 87C99 88 104 88 108 87C113 85 117 83 121 80C124 78 127 76 128 74C126 76 122 78 118 80C114 82 109 83 103 83C97 83 92 82 87 80C83 78 80 76 77 75Z',
          fill: '#D2940A',
        },
      },
    ],
  },
  windows: {
    viewBox: '0 0 42 43',
    multiColor: true,
    elements: [
      {
        tag: 'polygon',
        attrs: { points: '18.502,3.389 18.502,20.219 41.727,20.219 41.727,0', fill: '#001589' },
      },
      {
        tag: 'polygon',
        attrs: { points: '0,20.219 16.865,20.219 16.865,3.627 0,6.087', fill: '#001589' },
      },
      {
        tag: 'polygon',
        attrs: { points: '16.865,38.655 16.865,21.855 0,21.855 0,36.167', fill: '#001589' },
      },
      {
        tag: 'polygon',
        attrs: {
          points: '41.727,21.855 18.502,21.855 18.502,38.897 41.727,42.323',
          fill: '#001589',
        },
      },
    ],
  },
  'premium-hosting': {
    viewBox: '0 0 16 16',
    multiColor: true,
    elements: [
      {
        tag: 'circle',
        attrs: { cx: '8', cy: '8', r: '7.5', fill: '#3E9861' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M10.2 3H7.8L6 8.2H8.5L5.8 13L11 7.2H8.5L10.2 3Z',
          fill: 'white',
        },
      },
    ],
  },
};
